export interface SpreadSeoData {
  slug: string;
  id: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  /** Card-stock colour of the spread's patch — the same as its tile on the app's /spread home. */
  stock: string;
  /** Fabric the patch is cut from (a `.fabric-*` role), tone-on-tone over `stock`. */
  fabric: string;
  free: boolean;
  cardCount: number;
  readingRoute: string;
  content: {
    intro: string;
    howItWorks: string[];
    whatYouLearn: string[];
    whoIsItFor: string;
    faq: Array<{ question: string; answer: string }>;
  };
  /** Optional cross-link to a paired informational guide (SEO de-orphaning). */
  guide?: { to: string; label: string };
  /**
   * SEO pilot (docs/seo/competitors-spreads-2026-10.md §2.2): an on-page quick draw
   * (`SpreadQuickDraw` — face-down cards → turn over → short meaning per card + /karty links)
   * under the hero. For the 4 pilot pages only; the control pages stay unchanged.
   * Shipped OFF: switch the 4 `quickDraw: false` entries to true once the pilot copy has been
   * live 1–2 weeks, so its effect is measured separately (roadmap §1.1б).
   */
  quickDraw?: boolean;
  /**
   * SEO pilot (docs/seo/roadmap-2026-10.md): reviewed long-form copy for the 4 test pages.
   * When present, SpreadDetail takes meta / H1 / intro / body / FAQ from here and adds a
   * worked example + an «Все расклады» list. When absent (the 12 control pages) the page
   * renders exactly as before — keep it that way until the experiment is read out.
   */
  pilot?: SpreadSeoPilot;
}

export interface SpreadSeoLink {
  to: string;
  label: string;
}

export interface SpreadSeoSection {
  h2: string;
  paragraphs: string[];
  bullets?: string[];
  /** Internal links under the section (rendered as RouterLinks → real <a href> in the SSG HTML). */
  links?: SpreadSeoLink[];
}

export interface SpreadSeoExample {
  /** The querent's question / what she had in mind, as written. */
  question: string;
  /** The drawn cards in order; `cardSlug` is a /karty slug (CARD_SEO_DATA key). */
  cards: Array<{ cardSlug: string; position: string; text: string }>;
  summary: string;
}

export interface SpreadSeoPilot {
  metaTitle: string;
  metaDescription: string;
  h1: string;
  /** Paragraphs separated by a blank line (\n\n). */
  intro: string;
  sections: SpreadSeoSection[];
  example: SpreadSeoExample;
  faq: Array<{ question: string; answer: string }>;
}

export const SPREAD_SEO_DATA: Record<string, SpreadSeoData> = {
  'karta-dnya': {
    slug: 'karta-dnya',
    id: 'daily',
    title: 'Карта дня',
    metaTitle: 'Карта Дня Таро Онлайн Бесплатно | Таро Мяу',
    metaDescription: 'Вытяни карту дня таро онлайн бесплатно и узнай, что ждёт тебя сегодня. Персональная интерпретация и совет на день от Таро Мяу.',
    stock: 'bg-card',
    fabric: 'fabric-velvet',
    free: true,
    cardCount: 1,
    readingRoute: '/reading/daily',
    quickDraw: false,
    content: {
      intro:
        'Карта дня таро онлайн бесплатно — это твой маленький утренний ритуал, который задаёт настроение на весь день. Всего одна карта, но сколько в ней смысла! Она не предсказывает будущее буквально — скорее, подсвечивает энергию дня, помогает заметить то, что ты могла бы упустить. Может, сегодня стоит быть осторожнее в словах. Или наоборот — день идеален, чтобы рискнуть и сказать то, что давно хотела. Карта дня работает как зеркало: ты смотришь на неё и видишь то, что уже знала, но не решалась признать. Это бесплатно, быстро и удивительно точно — попробуй прямо сейчас и почувствуй разницу.',
      howItWorks: [
        'Сделай глубокий вдох и подумай: «Что мне важно знать сегодня?»',
        'Вытяни одну карту из колоды',
        'Получи персональную интерпретацию от Кота — не шаблон, а разбор именно для тебя',
        'Возвращайся каждый день — со временем ты начнёшь замечать паттерны',
      ],
      whatYouLearn: [
        'Главная энергия и настроение дня',
        'На что обратить внимание в ближайшие часы',
        'Совет, который поможет прожить день осознаннее',
      ],
      whoIsItFor:
        'Для тебя, если хочется начинать утро с чего-то осмысленного. Если устала от бесконечного скроллинга и ищешь минуту тишины для себя. Карта дня — идеальный первый шаг в мир таро, даже если ты никогда раньше не держала колоду в руках.',
      faq: [
        {
          question: 'Можно ли тянуть карту дня несколько раз?',
          answer:
            'Технически — да, но лучше всего работает одна карта в день. Она задаёт фокус, и перетягивание размывает смысл. Доверься первой карте — она обычно самая точная.',
        },
        {
          question: 'В какое время лучше тянуть карту дня?',
          answer:
            'Утром, до того как день затянет в свою рутину. Но строгих правил нет — если вспомнила в обед, тяни в обед. Главное — момент спокойствия и внимания к себе.',
        },
        {
          question: 'Карта дня — это серьёзно или просто развлечение?',
          answer:
            'Это ровно то, чем ты захочешь это сделать. Для кого-то это весёлый ритуал, для кого-то — инструмент саморефлексии. Мы не обещаем магию, но обещаем повод задуматься.',
        },
      ],
    },
    // SEO pilot 2026-10 (docs/seo/roadmap-2026-10.md): reviewed copy — replaces the page's
    // meta, H1, intro, body sections and FAQ, and adds a worked example. Control pages have no `pilot`.
    pilot: {
      metaTitle: 'Таро карта дня на сегодня — онлайн бесплатно | Таро Мяу',
      metaDescription: 'Таро карта дня онлайн бесплатно: выбери одну карту из пяти и получи разбор от Таро Кота — настроение дня и совет, на что обратить внимание.',
      h1: 'Таро карта дня на сегодня',
      intro: 'Таро карта дня — это одна карта, которую ты вытягиваешь утром, чтобы понять настроение дня и получить совет, на что обратить внимание. Её можно вытянуть онлайн и бесплатно, раз в сутки, и на это уходит пара минут: выбрала карту, прочитала разбор от Таро Кота — и дальше в свой день.\n\nКарта не решает за тебя и не предсказывает, напишет ли он. Она работает как взгляд со стороны: подсвечивает то, что ты, возможно, и сама чувствуешь, но не успела сформулировать.',
      sections: [
        {
          h2: 'Схема расклада: одна карта на один день',
          paragraphs: [
            'Перед тобой пять карт рубашкой вверх. Выбираешь одну — ту, к которой тянется рука. Это и есть твоя карта дня — единственная позиция в раскладе.',
            'Таро Кот разбирает её именно как карту этого дня: какое настроение у этого дня, какие возможности легко проглядеть и какой совет поможет прожить его спокойнее. Перевёрнутых карт здесь нет. Следующую карту можно вытянуть завтра.',
          ],
        },
        {
          h2: 'Какой вопрос задать карте дня',
          paragraphs: [
            'Писать вопрос не нужно — эта карта вытягивается без него, и разбор будет про твой день в целом. Но если держать в голове, что тебя сейчас волнует, тебе будет проще приложить разбор к своей ситуации. Лучше всего работают вопросы «как» и «на что обратить внимание», а не «случится ли»:',
          ],
          bullets: [
            'Что мне важно помнить сегодня в разговоре с ним?',
            'Как не накрутить себя, если он опять будет молчать?',
            'На что обратить внимание на работе сегодня?',
            'Где мне сегодня взять сил?',
            'Какой совет дня мне нужен прямо сейчас?',
          ],
          links: [
            { to: '/kak-zadavat-voprosy-taro', label: 'Как задавать вопросы таро' },
            { to: '/rasklady/zadaj-vopros', label: 'Ответ одной картой на конкретный вопрос' },
          ],
        },
        {
          h2: 'Как читать свою карту дня',
          paragraphs: [
            'Смотри на карту как на линзу, а не как на приговор. Утром прочитай разбор и выбери из него одну мысль, которую возьмёшь с собой. Вечером вернись к ней: где карта откликнулась, а где прошла мимо.',
            'Тяжёлые карты — Смерть, Башня, Десятка Мечей — не стоит приукрашивать: день может быть непростым, и карта честно об этом предупреждает. Но это не приговор. Чаще она говорит о том, что что-то пора закончить, отпустить или перестать тянуть на себе. Светлые карты тоже не гарантия: они показывают, где сегодня легче, но действовать всё равно тебе.',
          ],
          links: [
            { to: '/karty/smert', label: 'Смерть в таро' },
            { to: '/karty/bashnya', label: 'Башня в таро' },
            { to: '/karty/desyatka-mechej', label: 'Десятка Мечей в таро' },
            { to: '/karty', label: 'Значения всех 78 карт' },
            { to: '/karty/sochetaniya', label: 'Сочетания карт таро' },
          ],
        },
        {
          h2: 'Если выпала карта масти: кубки, мечи, пентакли, жезлы',
          paragraphs: [
            'Масть сразу подсказывает, про какую сферу будет день. Старший аркан обычно означает тему крупнее одного дня — к ней стоит присмотреться внимательнее.',
          ],
          bullets: [
            'Кубки — чувства, близость, отношения. День про то, что ты чувствуешь и с кем.',
            'Мечи — мысли, слова, решения. Стоит следить за тем, что говоришь и что себе накручиваешь.',
            'Пентакли — работа, деньги, тело, быт. День про практичные дела и заботу о себе.',
            'Жезлы — энергия, желания, инициатива. День про то, куда направить силы: начать отложенное или, наоборот, снять с себя лишнее.',
          ],
          links: [
            { to: '/karty/masti/kubki', label: 'Масть кубков' },
            { to: '/karty/masti/mechi', label: 'Масть мечей' },
            { to: '/karty/masti/pentakli', label: 'Масть пентаклей' },
            { to: '/karty/masti/zhezly', label: 'Масть жезлов' },
          ],
        },
        {
          h2: 'Карта дня как ежедневная практика',
          paragraphs: [
            'Самое интересное начинается через пару недель. Записывай, какая карта выпала и как прошёл день, — и ты заметишь повторы: одна и та же масть, похожие советы, темы, к которым ты возвращаешься. В этом и есть честный смысл таро на сегодня: не про судьбу, а про тебя.',
            'Если одной карты мало и хочется разобрать ситуацию целиком, возьми расклад побольше. Расклад на три карты бесплатный, а расклад на любовь и отношения входит в подписку.',
          ],
          links: [
            { to: '/rasklady/rasklad-na-tri-karty', label: 'Расклад на три карты' },
            { to: '/rasklady/lyubovnyj-rasklad', label: 'Расклад на любовь и отношения' },
          ],
        },
      ],
      example: {
        question: 'Держала в голове: вечером разговор с ним после ссоры.',
        cards: [
          {
            cardSlug: 'pazh-mechej',
            position: 'Карта дня',
            text: 'Паж Мечей — карта любопытства, прямых вопросов и острых слов. Его совет на день: если что-то тревожит, спросить прямо, а не строить догадки в голове. Если приложить этот совет к вечернему разговору, получается так: не превращать его в допрос, сначала выслушать, потом делать выводы. Острое слово легко ранит, поэтому важные фразы лучше выбирать бережно.',
          },
        ],
        summary: 'День про честный разговор. Карта не говорит, помиритесь вы или нет. Её совет — спрашивать прямо, слушать внимательно и не торопиться с выводами, и его легко приложить к вечернему разговору.',
      },
      faq: [
        {
          question: 'Можно ли вытянуть карту дня на завтра?',
          answer: 'Карта описывает тот день, в который ты её тянешь. Новая открывается каждое утро, так что карту на завтра проще всего вытянуть завтра. Если волнует конкретное событие впереди, лучше подойдёт расклад на вопрос.',
        },
        {
          question: 'Что значит перевёрнутая карта дня?',
          answer: 'В Таро Мяу карта дня всегда выпадает в прямом положении, и разбор строится по её прямому значению. Если тебе интересно перевёрнутое значение, оно есть на странице каждой карты.',
        },
        {
          question: 'Можно ли тянуть карту дня на мужчину?',
          answer: 'Эта карта — про твой день и про тебя. Если хочется разобраться в ситуации с ним, начни с бесплатного расклада на три карты или ответа одной картой на конкретный вопрос. Для более глубокого разбора есть расклад на любовь и отношения: в нём отдельные позиции про его чувства и его действия, и он входит в подписку.',
        },
        {
          question: 'Можно ли перетянуть карту, если не понравилась?',
          answer: 'Одна карта в сутки — и это к лучшему. Перетягивание размывает смысл. Если карта кажется непонятной, побудь с ней до вечера: часто к концу дня становится ясно, о чём она была.',
        },
        {
          question: 'Карта дня бесплатная?',
          answer: 'Да. Одна карта с разбором от Таро Кота доступна каждый день бесплатно, без подписки.',
        },
        {
          question: 'Карта дня может предсказать будущее?',
          answer: 'Мы смотрим на таро как на способ подумать о себе, а не как на приговор судьбы. Карта подсветит настроение и даст совет, а решения остаются за тобой. В вопросах здоровья, денег и документов опирайся на специалистов.',
        },
      ],
    },
  },

  'da-ili-net': {
    slug: 'da-ili-net',
    id: 'yes-no',
    title: 'Да или Нет',
    metaTitle: 'Да или Нет — Таро Онлайн | Таро Мяу',
    metaDescription: 'Да или нет таро онлайн — быстрый и чёткий ответ на твой вопрос. 3 карты, персональная интерпретация и честный расклад без воды.',
    stock: 'bg-stock-wine',
    fabric: 'fabric-quilted',
    free: true,
    cardCount: 3,
    readingRoute: '/reading/yes-no',
    content: {
      intro:
        'Бывают моменты, когда нужен не длинный разбор, а простой ответ — да или нет. Таро онлайн даёт именно это: три карты, которые складываются в чёткий ответ на твой вопрос. Этот расклад не про размытые «может быть» — он прямой и конкретный. Задай вопрос, на который можно ответить «да» или «нет», и карты покажут, куда склоняется чаша весов. Стоит ли писать первой? Выгорит ли эта затея? Пора ли менять работу? Иногда мы уже знаем ответ, но нам нужно, чтобы кто-то подтвердил. Этот расклад как честная подруга — скажет как есть, без лишних церемоний. Три карты, пара минут — и ты знаешь, на чём стоишь.',
      howItWorks: [
        'Сформулируй вопрос так, чтобы на него можно было ответить «да» или «нет»',
        'Вытяни 3 карты из колоды',
        'Кот проанализирует расклад и даст ясный ответ с пояснением',
        'Прочти детальную интерпретацию — она объяснит, почему именно так',
      ],
      whatYouLearn: [
        'Однозначный ответ на конкретный вопрос',
        'Скрытые факторы, влияющие на ситуацию',
        'Совет, как действовать с учётом ответа',
      ],
      whoIsItFor:
        'Для тебя, если мучаешься вопросом и бесконечно прокручиваешь варианты в голове. Если нужна точка опоры, чтобы наконец решиться. Этот расклад идеален, когда тебе нужна не терапия, а конкретика.',
      faq: [
        {
          question: 'Насколько точен расклад «Да или Нет»?',
          answer:
            'Расклад отражает текущую энергию ситуации. Он не высечен в камне — это скорее барометр, чем приговор. Чем точнее сформулирован вопрос, тем яснее ответ.',
        },
        {
          question: 'Можно ли задавать один и тот же вопрос несколько раз?',
          answer:
            'Лучше не стоит — повторное вытягивание обычно запутывает, а не проясняет. Доверься первому раскладу. Если ответ не нравится, это тоже информация.',
        },
      ],
    },
  },

  'zadaj-vopros': {
    slug: 'zadaj-vopros',
    id: 'question-single',
    title: 'Задай вопрос',
    metaTitle: 'Таро на Вопрос Онлайн — 1 Карта | Таро Мяу',
    metaDescription: 'Таро на вопрос онлайн — задай свой вопрос и получи ответ одной картой. Бесплатный расклад с персональной интерпретацией.',
    stock: 'bg-stock-plum',
    fabric: 'fabric-knit',
    free: true,
    cardCount: 1,
    readingRoute: '/reading/question-single',
    guide: { to: '/kak-zadavat-voprosy-taro', label: 'Как задавать вопросы Таро' },
    content: {
      intro:
        'Таро на вопрос онлайн — самый прямой способ получить ответ. У тебя есть вопрос, и тебе нужна не история на полчаса, а одна чёткая мысль, которая расставит всё по местам. Одна карта — как луч фонарика в темноте: не освещает всю комнату, но точно показывает, куда смотреть. Задай любой вопрос — про отношения, работу, решение, которое не даёт покоя. Карта ответит честно, без лишнего. Этот расклад идеален, когда ты не хочешь углубляться в длинный анализ, а просто ищешь подсказку. Один вопрос, одна карта, один ответ — и неожиданно становится яснее.',
      howItWorks: [
        'Сформулируй свой вопрос — чем конкретнее, тем точнее ответ',
        'Вытяни одну карту',
        'Получи развёрнутую интерпретацию, привязанную к твоему вопросу',
      ],
      whatYouLearn: [
        'Ответ на конкретный вопрос через призму выпавшей карты',
        'Аспекты ситуации, которые ты могла не учитывать',
        'Направление, в котором стоит двигаться',
      ],
      whoIsItFor:
        'Для тебя, если конкретный вопрос не даёт покоя и нужна свежая перспектива. Если не хочешь долгих раскладов — просто быстрый и точный ответ. Идеально для тех, кто ценит лаконичность.',
      faq: [
        {
          question: 'Какой вопрос лучше задавать таро?',
          answer:
            'Открытые вопросы работают лучше закрытых. Вместо «Он меня любит?» попробуй «Что мне важно понять про наши отношения?». Чем больше пространства для ответа, тем глубже будет инсайт.',
        },
        {
          question: 'Можно ли задавать вопросы про других людей?',
          answer:
            'Да, но с оговоркой: карты покажут ситуацию с твоей стороны. Таро лучше всего работает, когда фокусируется на тебе и твоих решениях, а не на чужих мыслях.',
        },
      ],
    },
  },

  'rasklad-na-tri-karty': {
    slug: 'rasklad-na-tri-karty',
    id: 'question-three',
    title: 'Расклад на три карты',
    metaTitle: 'Расклад на 3 Карты Таро Онлайн | Таро Мяу',
    metaDescription: 'Расклад на три карты таро — прошлое, настоящее и будущее. Бесплатный глубокий разбор твоего вопроса с персональной интерпретацией.',
    stock: 'bg-stock-tide',
    fabric: 'fabric-denim',
    free: true,
    cardCount: 3,
    readingRoute: '/reading/question-three',
    quickDraw: false,
    content: {
      intro:
        'Расклад на три карты таро — это классика, которая никогда не подводит. Три позиции — прошлое, настоящее и будущее — дают объёмную картину любой ситуации. Первая карта расскажет, что привело тебя сюда, какие события и решения сформировали текущий момент. Вторая покажет, где ты сейчас — честно, без прикрас. Третья приоткроет направление, в котором всё движется. Это не гадание на кофейной гуще, а инструмент для осмысления. Когда видишь свою историю разложенной на три карты, паззл складывается сам. Бесплатный расклад, который даёт больше, чем можно ожидать от трёх карт.',
      howItWorks: [
        'Подумай о ситуации или вопросе, который тебя волнует',
        'Вытяни 3 карты — они займут позиции прошлого, настоящего и будущего',
        'Получи подробную интерпретацию каждой карты и их взаимосвязи',
        'Обрати внимание на общую историю — она часто важнее отдельных карт',
      ],
      whatYouLearn: [
        'Что из прошлого влияет на текущую ситуацию',
        'Реальная картина настоящего момента',
        'Куда движется ситуация при текущем развитии событий',
        'Связи между этапами, которые раньше были незаметны',
      ],
      whoIsItFor:
        'Для тебя, если одной карты мало и хочется увидеть полную картину. Если нужно понять не просто «что делать», а откуда пришла ситуация и к чему ведёт. Отличный выбор для первого серьёзного расклада.',
      faq: [
        {
          question: 'Чем расклад на три карты отличается от одной карты?',
          answer:
            'Одна карта — это фокус на моменте, ответ-вспышка. Три карты дают историю: контекст, текущее состояние и направление. Если одна карта — это фотография, то три карты — короткий фильм.',
        },
        {
          question: 'Обязательно ли три карты — это прошлое, настоящее, будущее?',
          answer:
            'Это самая популярная схема, но три карты можно читать и как «ситуация — препятствие — совет» или «ты — другой человек — отношения». Кот адаптирует интерпретацию под твой вопрос.',
        },
      ],
    },
    // SEO pilot 2026-10 (docs/seo/roadmap-2026-10.md): reviewed copy — replaces the page's
    // meta, H1, intro, body sections and FAQ, and adds a worked example. Control pages have no `pilot`.
    pilot: {
      metaTitle: 'Расклад на три карты: таро 3 карты онлайн бесплатно | Таро Мяу',
      metaDescription: 'Расклад таро на 3 карты онлайн бесплатно: схема, какие вопросы задать и пример расклада на отношения. Кот прочитает три карты как одну историю.',
      h1: 'Расклад на три карты таро',
      intro: 'Расклад на три карты — короткий расклад таро на один вопрос: ты выбираешь три карты, и они читаются по порядку как одна история с советом в конце. Его делают на ситуацию, на ближайшее будущее и, конечно, на отношения — когда одной карты мало, а большой расклад пока ни к чему.\n\nВ Таро Мяу его можно сделать онлайн и бесплатно, без регистрации: напиши вопрос, выбери три карты из пяти, и кот разберёт их вместе с твоей ситуацией. Карты не решают за тебя и не предсказывают судьбу. Они помогают посмотреть на происходящее со стороны и заметить то, что ты, возможно, и так чувствуешь.',
      sections: [
        {
          h2: 'Схема расклада: как кот раскладывает три карты',
          paragraphs: [
            'Сначала ты пишешь вопрос — без него расклад не начнётся, потому что кот читает карты именно через него. Потом перед тобой пять карт рубашкой вверх: выбери три, на которых задерживается взгляд. Порядок важен: первая, вторая и третья карты складываются в историю, а в конце кот подскажет, как действовать дальше.',
            'Жёстких подписей у позиций нет, и это удобно: схему задаёт твой вопрос. Хочешь классику — добавь её в вопрос, и кот постарается читать карты по этой схеме:',
          ],
          bullets: [
            '«Что было, что есть, к чему идёт» — прошлое, настоящее и ближайшее будущее.',
            '«Ситуация, что мешает, что делать» — когда всё запуталось и непонятно, с какой стороны подойти.',
            '«Я, он и что между нами» — когда вопрос про отношения.',
          ],
          links: [
            { to: '/rasklady/rasklad-sudby', label: 'Расклад судьбы — прошлое, настоящее и будущее в готовой схеме' },
          ],
        },
        {
          h2: 'Какие вопросы задать трём картам',
          paragraphs: [
            'Лучше всего работают открытые вопросы — те, что начинаются с «что», «как» и «почему». На «да или нет» три карты ответят уклончиво, для этого есть отдельный расклад. Несколько примеров:',
            'Один вопрос — один расклад. Если в голове сразу три темы, разбери их по очереди, иначе карты будут отвечать на всё сразу и ни на что толком.',
          ],
          bullets: [
            'Про ситуацию: «Что на самом деле происходит у меня на работе?»',
            'Про ближайшее будущее: «Как будут развиваться наши отношения в ближайшие месяцы?»',
            'Про мужчину и чувства: «Что сейчас между мной и Сашей и что мне с этим делать?»',
            'Про себя: «Что мешает мне отпустить эту историю?»',
          ],
          links: [
            { to: '/kak-zadavat-voprosy-taro', label: 'Как задавать вопросы таро' },
            { to: '/rasklady/da-ili-net', label: 'Расклад «Да или нет»' },
          ],
        },
        {
          h2: 'Расклад на будущее: что три карты могут показать',
          paragraphs: [
            'Последнюю карту часто читают как «что будет», но честнее видеть в ней направление: куда ведёт ситуация, если всё останется как сейчас. Ход событий меняют твои решения, поэтому карты дают не приговор, а точку, от которой удобно думать.',
            'Лучше всего три карты смотрят на ближайшее время — недели, пару месяцев. Здоровье, суды и крупные денежные решения таро не предскажет: тут нужны врач, юрист или финансовый консультант. Карты могут помочь разве что разобраться, что ты сама чувствуешь по этому поводу.',
          ],
        },
        {
          h2: 'Как читать три карты: значение каждой и связь между ними',
          paragraphs: [
            'Сначала посмотри на каждую карту отдельно — значение есть на её странице в нашем справочнике. Потом на историю целиком: что меняется от первой карты к третьей, где напряжение, а где становится легче. Несколько подсказок для расшифровки:',
          ],
          bullets: [
            'Много Старших арканов — тема большая, она про тебя, а не про один эпизод.',
            'Масти подсказывают сферу: Кубки — чувства, Мечи — мысли, тревога и слова, Пентакли — деньги, тело и быт, Жезлы — энергия и действия.',
            'Соседние карты усиливают или смягчают друг друга: тяжёлая карта рядом со светлой — это трудность, у которой есть выход.',
          ],
          links: [
            { to: '/karty', label: 'Значения всех 78 карт таро' },
            { to: '/karty/masti/kubki', label: 'Масть Кубков' },
            { to: '/karty/masti/mechi', label: 'Масть Мечей' },
            { to: '/karty/masti/pentakli', label: 'Масть Пентаклей' },
            { to: '/karty/masti/zhezly', label: 'Масть Жезлов' },
            { to: '/karty/bashnya', label: 'Башня — значение карты' },
            { to: '/karty/desyatka-mechej', label: 'Десятка Мечей — значение карты' },
            { to: '/karty/sochetaniya', label: 'Сочетания карт таро' },
          ],
        },
        {
          h2: 'Когда трёх карт мало',
          paragraphs: [
            'Три карты хороши для одного ясного вопроса. Если ситуация многослойная или хочется отдельно увидеть его чувства, твои и ваше будущее, возьми расклад побольше. А если нужен просто ориентир на день, хватит одной карты.',
          ],
          links: [
            { to: '/rasklady/lyubovnyj-rasklad', label: 'Расклад на любовь и отношения — 6 карт' },
            { to: '/rasklady/gadanie-na-situaciyu', label: 'Гадание на ситуацию — 4 карты' },
            { to: '/rasklady/rasklad-na-byvshego', label: 'Расклад на бывшего — 4 карты' },
            { to: '/rasklady/karta-dnya', label: 'Карта дня — 1 карта' },
          ],
        },
      ],
      example: {
        question: 'Он стал отвечать реже и суше. Что сейчас между нами и к чему это ведёт?',
        cards: [
          {
            cardSlug: 'chetverka-kubkov',
            position: 'Первая карта',
            text: 'История начинается с Четвёрки Кубков — скуки и охлаждения, когда отношения стали привычными, а чувства будто застыли. Это не обязательно «он разлюбил»: карта говорит о пресыщении и усталости, и они могут касаться вас обоих. Стоит честно спросить себя, не начали ли вы принимать друг друга как должное.',
          },
          {
            cardSlug: 'dvoyka-mechej',
            position: 'Вторая карта',
            text: 'В центре — Двойка Мечей, пауза и избегание. Похоже, вы оба держите хрупкое перемирие и обходите честный разговор, чтобы не было больно, а короткие ответы — тоже способ ничего не решать. Повязка на глазах подсказывает: ты уже многое чувствуешь, просто пока не готова это признать.',
          },
          {
            cardSlug: 'pazh-kubkov',
            position: 'Третья карта',
            text: 'Паж Кубков показывает, куда история может повернуть: нежное начало, тёплое сообщение, разговор по душам. Это не обещание, что всё вернётся как было, а подсказка, какой шаг сейчас живой: маленький искренний знак внимания без давления и выяснения отношений. Как он ответит — уже его выбор.',
          },
        ],
        summary: 'Вместе карты рассказывают историю «остыли — замолчали — можно снова заговорить». Совет кота: не гадать по длине его сообщений, а спокойно сказать, что ты замечаешь и чего хочешь сама. Если ответа не будет, это тоже ответ, и он скажет больше, чем ещё десять раскладов.',
      },
      faq: [
        {
          question: 'Можно ли сделать расклад на 3 карты онлайн бесплатно?',
          answer: 'Да. Без регистрации, и первые расклады бесплатны — хватит, чтобы спокойно разобрать свой вопрос. Подписка нужна, только если захочешь гадать без ограничений и открыть премиум-расклады.',
        },
        {
          question: 'Можно ли сделать расклад на мужчину или на его чувства?',
          answer: 'Можно, но карты покажут ситуацию с твоей стороны: что происходит между вами и что ты можешь сделать. Читать чужие мысли наверняка таро не умеет, и честный кот не будет делать вид, что умеет.',
        },
        {
          question: 'Что делать, если выпали тяжёлые карты?',
          answer: 'Не паниковать. Башня или Десятка Мечей — не приговор, а сигнал, на что обратить внимание. Посмотри, что лежит рядом, и прочитай совет: тяжёлая карта чаще показывает, где болит, чем то, чем всё закончится.',
        },
        {
          question: 'Можно ли повторить расклад на тот же вопрос?',
          answer: 'Можно, но лучше не сразу. Если перетягивать карты, пока не выпадет нужный ответ, ясности не прибавится — только тревоги. Подожди, пока что-то изменится в ситуации или в тебе, и тогда спроси снова.',
        },
        {
          question: 'Чем три карты отличаются от одной?',
          answer: 'Одна карта — снимок момента. Три — короткая история: в ней есть развитие, связь между картами и совет. Поэтому три карты лучше подходят для вопросов, где важно понять, откуда что взялось и куда движется.',
        },
      ],
    },
  },

  'a-ili-b': {
    slug: 'a-ili-b',
    id: 'a-or-b',
    title: 'А или Б',
    metaTitle: 'Расклад на Выбор Таро — А или Б | Таро Мяу',
    metaDescription: 'Расклад на выбор таро — сравни два варианта с помощью карт. Кот покажет плюсы и минусы каждого пути и поможет принять решение.',
    stock: 'bg-stock-bronze',
    fabric: 'fabric-corduroy',
    free: false,
    cardCount: 2,
    readingRoute: '/reading/a-or-b',
    content: {
      intro:
        'Расклад на выбор таро «А или Б» — для тех моментов, когда стоишь на развилке и оба пути кажутся одинаково привлекательными (или одинаково страшными). Переезжать или остаться? Согласиться на предложение или подождать лучшего? Сказать правду или промолчать? Две карты — по одной на каждый вариант — подсвечивают энергию и последствия каждого выбора. Это не расклад, который скажет «делай так». Он покажет, что стоит за каждым вариантом — какие возможности откроются и какие подводные камни ждут. А решение, как всегда, останется за тобой. Потому что лучший выбор — тот, который ты делаешь осознанно.',
      howItWorks: [
        'Опиши два варианта, между которыми выбираешь',
        'Вытяни 2 карты — по одной на каждый путь',
        'Кот проанализирует энергию и перспективы каждого варианта',
        'Сравни результаты и прислушайся к своей интуиции',
      ],
      whatYouLearn: [
        'Энергия и потенциал каждого из двух вариантов',
        'Скрытые плюсы и минусы, которые ты могла не учесть',
        'Какой путь больше резонирует с тобой прямо сейчас',
      ],
      whoIsItFor:
        'Для тебя, если решение не даёт покоя и плюсы с минусами уже сто раз взвешены — а ясности больше не стало. Если нужна свежая перспектива, чтобы наконец перестать метаться и выбрать.',
      faq: [
        {
          question: 'Что если оба варианта показали плохие карты?',
          answer:
            'Это не приговор — это знак, что оба пути потребуют усилий. Посмотри, какие именно сложности подсвечивает каждая карта. Иногда полезно задуматься: а есть ли третий вариант, который ты не рассматривала?',
        },
        {
          question: 'Можно ли сравнивать больше двух вариантов?',
          answer:
            'Этот расклад заточен под два варианта — так анализ получается максимально фокусированным. Если вариантов больше, попробуй сначала отсеять лишние, а потом сравнить финалистов.',
        },
      ],
    },
  },

  'lyubovnyj-rasklad': {
    slug: 'lyubovnyj-rasklad',
    id: 'love',
    title: 'Любовный расклад',
    metaTitle: 'Расклад Таро на Любовь Онлайн | Таро Мяу',
    metaDescription: 'Расклад таро на любовь онлайн — узнай чувства партнёра, скрытые препятствия и перспективы отношений. 6 карт, глубокий личный разбор.',
    stock: 'bg-stock-rust',
    fabric: 'fabric-gingham',
    free: false,
    cardCount: 6,
    readingRoute: '/reading/love',
    quickDraw: false,
    content: {
      intro:
        'Расклад таро на любовь онлайн — это шесть карт, которые раскладывают твои отношения на составляющие. Что ты чувствуешь на самом деле, что чувствует он, что между вами стоит и к чему всё идёт. Любовь — самая частая причина, по которой люди обращаются к таро, и это понятно: сердце не слушает логику, а голова не может успокоить сердце. Этот расклад не заменит разговор с партнёром, но поможет понять, о чём стоит поговорить. Он подсвечивает слепые зоны — те вещи, которые ты чувствуешь, но не можешь сформулировать. Шесть карт, шесть граней отношений — и картина, которая наконец складывается в нечто осмысленное.',
      howItWorks: [
        'Подумай о человеке и ваших отношениях — не формулируй вопрос, просто сосредоточься на чувствах',
        'Вытяни 6 карт — каждая отвечает за свой аспект отношений',
        'Получи глубокий разбор: твои чувства, его чувства, динамика, препятствия, советы, перспективы',
        'Обрати внимание на совет — он часто оказывается самой ценной частью расклада',
      ],
      whatYouLearn: [
        'Истинные чувства — твои и партнёра',
        'Скрытые препятствия и непроговорённые ожидания',
        'Динамика отношений: что работает и что мешает',
        'Перспективы развития и конкретные шаги',
      ],
      whoIsItFor:
        'Для тебя, если отношения занимают все мысли и ты не можешь разобраться в своих чувствах. Если хочется понять, что происходит между вами — не на уровне «он написал/не написал», а глубже. Если готова услышать правду, даже если она непростая.',
      faq: [
        {
          question: 'Можно ли делать расклад на любовь, если я не в отношениях?',
          answer:
            'Конечно! Расклад покажет, что тебе мешает встретить пару, какую энергию ты излучаешь и чего ждать в ближайшее время. Он работает и на конкретного человека, и на ситуацию в целом.',
        },
        {
          question: 'Расклад сказал, что перспективы плохие. Это приговор?',
          answer:
            'Нет. Карты показывают текущее направление — но ты можешь его изменить. Если расклад показал сложности, воспринимай это как карту местности: теперь ты знаешь, где ямы, и можешь их обойти.',
        },
        {
          question: 'Как часто можно делать расклад на любовь?',
          answer:
            'Раз в одну-две недели — оптимально. За это время ситуация успевает развиться. Делать каждый день — значит не дать карте дня даже шанса сбыться.',
        },
      ],
    },
    // SEO pilot 2026-10 (docs/seo/roadmap-2026-10.md): reviewed copy — replaces the page's
    // meta, H1, intro, body sections and FAQ, and adds a worked example. Control pages have no `pilot`.
    pilot: {
      metaTitle: 'Расклад таро на любовь и отношения онлайн | Таро Мяу',
      metaDescription: 'Расклад таро на любовь и отношения: 6 карт о его чувствах и поступках, твоих чувствах и о том, куда вы движетесь. Схема, пример, вопросы.',
      h1: 'Расклад таро на любовь и отношения',
      intro: 'Расклад таро на любовь и отношения в Таро Мяу — это шесть карт про вас двоих: что он чувствует и что делает, что чувствуешь и делаешь ты, где вы сейчас и куда всё движется. Кот читает все шесть карт как одну историю и пишет личный разбор простыми словами — онлайн, на сайте или в Telegram.\n\nКарты не решают за тебя и не выносят приговор. Они помогают посмотреть на отношения со стороны и понять, о чём стоит поговорить — с ним или с собой.',
      sections: [
        {
          h2: 'Схема расклада: 6 позиций',
          paragraphs: [
            'Схема зеркальная: первые две карты про него, следующие две — про тебя, последние две — про вас вместе. Так сразу видно, где чувства и поступки совпадают, а где расходятся.',
          ],
          bullets: [
            'Его чувства — что он, судя по картам, испытывает к тебе, независимо от того, что говорит вслух.',
            'Его действия — как он ведёт себя в отношениях.',
            'Твои чувства — то, что ты чувствуешь, даже если неловко признаться себе.',
            'Твои действия — что ты делаешь или не делаешь и как это на вас влияет.',
            'Сейчас — где ваши отношения находятся в этот момент.',
            'Ваше будущее — куда отношения движутся сейчас и какие возможности для вас открываются.',
          ],
        },
        {
          h2: 'Расклад на мужчину: его чувства и поступки',
          paragraphs: [
            'Чаще всего сюда приходят с одним вопросом: что он чувствует на самом деле. Поэтому чувства и действия разведены по разным позициям. Бывает, что он чувствует много, а делает мало, — или заботится, но держит дистанцию. На двух соседних картах это хорошо видно.',
            'Карты показывают тенденцию, а не читают мысли. Если важно знать наверняка, лучше спросить его — расклад поможет подобрать слова. Если вы расстались или он перестал выходить на связь, точнее подойдут отдельные расклады.',
          ],
          links: [
            { to: '/rasklady/rasklad-na-byvshego', label: 'Расклад на бывшего' },
            { to: '/rasklady/pochemu-on-propal', label: 'Почему он пропал?' },
          ],
        },
        {
          h2: 'Если ты сейчас одна: расклад на будущую любовь',
          paragraphs: [
            'Этот расклад построен вокруг конкретного человека — даже если вы только познакомились или всё пока на уровне симпатии. Но если человека нет, позициям «Его чувства» и «Его действия» не на кого опереться.',
            'Для вопросов про ближайшую любовь подойдёт расклад на три карты — он доступен без подписки. Спроси, например: «Что мне мешает встретить своего человека?»',
          ],
          links: [
            { to: '/rasklady/rasklad-na-tri-karty', label: 'Расклад на три карты' },
            { to: '/rasklady/zadaj-vopros', label: 'Задай вопрос' },
          ],
        },
        {
          h2: 'Какие вопросы задать в раскладе на любовь',
          paragraphs: [
            'Вводить вопрос здесь не нужно: карты вытягиваются сразу и читаются как история о вас двоих. Но перед раскладом подумай о конкретном человеке и о том, что волнует больше всего. Держи в голове открытый вопрос, а не «да или нет»:',
          ],
          bullets: [
            'Куда движутся наши отношения?',
            'Что мешает нам стать ближе?',
            'Почему между нами повторяется одна и та же ссора?',
            'Как мне вести себя, чтобы не потерять себя в этих отношениях?',
          ],
          links: [
            { to: '/kak-zadavat-voprosy-taro', label: 'Как задавать вопросы таро' },
            { to: '/rasklady/da-ili-net', label: 'Расклад «Да или нет»' },
          ],
        },
        {
          h2: 'Значение карт в любовном раскладе: масти и сочетания',
          paragraphs: [
            'Масть подсказывает, в какой плоскости лежит ответ. Кубки — про чувства и близость. Мечи — про мысли, тревогу, разговоры и конфликты. Пентакли — про надёжность, быт и реальные поступки. Жезлы — про страсть и инициативу. Старшие арканы — про крупные темы и силы, которые задают тон всем отношениям, а не одному эпизоду.',
            'Карты стоит читать вместе. Кубки в его чувствах и Пентакли в действиях — чувство, которое он подтверждает делами и заботой. Кубки в чувствах и Мечи в действиях — чувство есть, но в поступках много сомнений и недосказанности.',
          ],
          links: [
            { to: '/karty/masti/kubki', label: 'Масть Кубков' },
            { to: '/karty/masti/mechi', label: 'Масть Мечей' },
            { to: '/karty/masti/pentakli', label: 'Масть Пентаклей' },
            { to: '/karty/masti/zhezly', label: 'Масть Жезлов' },
            { to: '/karty/sochetaniya', label: 'Сочетания карт таро' },
            { to: '/karty', label: 'Значения всех 78 карт' },
          ],
        },
        {
          h2: 'Как читать расклад таро на отношения',
          paragraphs: [
            'Начни с пар: сравни его чувства со своими, его действия — со своими. Разрыв внутри пары часто важнее любой отдельной карты. Частые ошибки толкования:',
          ],
          bullets: [
            'Читать карту в отрыве от пары: смысл часто виден только в сравнении.',
            'Принимать «Его чувства» за чтение мыслей: карта показывает тенденцию, а не то, что у него в голове.',
            'Считать «Ваше будущее» приговором: это направление, и оно меняется вместе с вашими шагами.',
            'Перекладывать расклад, пока не выпадет нужный ответ: новые карты только запутывают.',
          ],
        },
      ],
      example: {
        question: 'Мы встречаемся полгода. Он тёплый, но о будущем не говорит, а я боюсь спросить. Перед раскладом думала об одном: куда идут наши отношения?',
        cards: [
          {
            cardSlug: 'rytsar-kubkov',
            position: 'Его чувства',
            text: 'Его чувства романтичные и живые: ему нравится ухаживать и быть рядом с тобой. Рыцарь Кубков — человек красивых жестов. Подкрепляются ли они поступками, покажет следующая карта.',
          },
          {
            cardSlug: 'chetverka-pentaklej',
            position: 'Его действия',
            text: 'В поступках он осторожен: держится за то, что есть, и не спешит открываться или что-то менять — скорее из страха потерять спокойствие, чем от равнодушия. Отсюда и молчание о будущем.',
          },
          {
            cardSlug: 'devyatka-mechej',
            position: 'Твои чувства',
            text: 'Внутри у тебя много тревоги: ты прокручиваешь его молчание по ночам и додумываешь худшее. Карта честно говорит, что часть этой боли рождается в мыслях, а не в его поступках.',
          },
          {
            cardSlug: 'dvoyka-mechej',
            position: 'Твои действия',
            text: 'И ты держишь паузу: не задаёшь главный вопрос и делаешь вид, что всё в порядке. Двойка Мечей — повязка на глазах, чтобы не пришлось решать. Пока вы оба молчите, всё стоит на месте.',
          },
          {
            cardSlug: 'umerennost',
            position: 'Сейчас',
            text: 'При этом сами отношения ровные: без драм, с уважением и умением находить середину. Это хорошая опора для разговора — вы умеете слышать друг друга.',
          },
          {
            cardSlug: 'dvoyka-kubkov',
            position: 'Ваше будущее',
            text: 'Направление тёплое: Двойка Кубков — союз на равных и взаимное чувство. Это возможность, а не гарантия, и ближе к ней ведёт шаг навстречу, а не ожидание, пока он заговорит первым.',
          },
        ],
        summary: 'Чувства с его стороны есть, тревога с твоей — тоже, а между ними общее молчание. Расклад не обещает, что всё будет хорошо, но подсказывает шаг: спокойно спросить, как он видит вас дальше. Опора для такого разговора у вас есть — Умеренность в настоящем говорит об умении слышать друг друга.',
      },
      faq: [
        {
          question: 'Можно ли сделать расклад на мужчину, с которым мы не встречаемся?',
          answer: 'Да. Подойдёт любой человек, о котором ты думаешь: тот, кто нравится, с кем вы недавно познакомились или с кем общение на паузе.',
        },
        {
          question: 'Подойдёт ли расклад, если я одна и хочу узнать о будущей любви?',
          answer: 'Он рассчитан на конкретного человека, поэтому без него половина позиций повиснет в воздухе. Для вопроса о ближайшей любви лучше взять расклад на три карты — он доступен без подписки.',
        },
        {
          question: 'Карты показали сложное будущее. Это приговор?',
          answer: 'Нет. «Ваше будущее» показывает, куда отношения движутся сейчас, а не то, что обязательно случится. Если карта тревожная, посмотри на позиции с действиями: там обычно видно, что можно изменить.',
        },
        {
          question: 'Как часто можно делать расклад таро на отношения?',
          answer: 'Раз в одну-две недели: за это время что-то успевает измениться. Если тянет спрашивать каждый день об одном и том же, это говорит о тревоге, а не о новых событиях. Тогда лучше выдохнуть и поговорить с подругой или психологом.',
        },
        {
          question: 'Это бесплатно?',
          answer: 'Расклад на любовь входит в подписку Таро Мяу. Без подписки, в пределах бесплатного лимита, доступны четыре расклада: карта дня, «Да или нет», «Задай вопрос» и расклад на три карты.',
        },
      ],
    },
  },

  'finansovyj-rasklad': {
    slug: 'finansovyj-rasklad',
    id: 'financial',
    title: 'Финансовый расклад',
    metaTitle: 'Финансовый Расклад Таро Онлайн | Таро Мяу',
    metaDescription: 'Финансовый расклад таро — разберись в денежных вопросах с помощью 4 карт. Личный разбор твоей финансовой ситуации и скрытых возможностей.',
    stock: 'bg-stock-sage',
    fabric: 'fabric-herringbone',
    free: false,
    cardCount: 4,
    readingRoute: '/reading/financial',
    content: {
      intro:
        'Финансовый расклад таро — это четыре карты, которые помогают разобраться в денежных вопросах без мистического тумана. Деньги — тема, о которой неудобно говорить, но которая не перестаёт волновать. Почему денег вечно не хватает? Стоит ли вкладываться в эту идею? Как перестать тревожиться о финансах? Этот расклад не покажет номер выигрышного лотерейного билета, но подсветит твои реальные установки по отношению к деньгам, покажет скрытые возможности и предупредит о рисках. Четыре карты — четыре грани твоей финансовой ситуации, и ясный взгляд на то, что можно сделать прямо сейчас.',
      howItWorks: [
        'Подумай о своём финансовом вопросе или ситуации',
        'Вытяни 4 карты из колоды',
        'Кот проанализирует каждую позицию: текущее положение, препятствия, возможности, совет',
        'Получи конкретные рекомендации, а не абстрактные обещания богатства',
      ],
      whatYouLearn: [
        'Реальное состояние твоих финансовых дел',
        'Внутренние блоки и установки, мешающие зарабатывать',
        'Скрытые возможности для улучшения ситуации',
        'Практический совет по следующему шагу',
      ],
      whoIsItFor:
        'Для тебя, если финансовые вопросы вызывают тревогу и ты чувствуешь, что застряла. Если хочется посмотреть на ситуацию с деньгами под новым углом — не через цифры в таблице, а через понимание глубинных причин.',
      faq: [
        {
          question: 'Таро может предсказать финансовый успех?',
          answer:
            'Таро не предсказывает конкретные суммы или даты. Но оно отлично показывает паттерны: почему деньги утекают, какие установки тебя ограничивают и где искать возможности. Это скорее финансовый коучинг через метафоры.',
        },
        {
          question: 'Стоит ли принимать инвестиционные решения по таро?',
          answer:
            'Таро — инструмент рефлексии, а не финансовый советник. Используй его для понимания своих страхов и мотивов, но конкретные денежные решения принимай с учётом реальных фактов и, при необходимости, с профессиональным консультантом.',
        },
      ],
    },
  },

  'rasklad-sudby': {
    slug: 'rasklad-sudby',
    id: 'destiny',
    title: 'Расклад Судьбы',
    metaTitle: 'Расклад Судьбы Таро Онлайн | Таро Мяу',
    metaDescription: 'Расклад судьбы таро — узнай своё предназначение и жизненный путь. 3 карты раскроют, к чему ты движешься и что тебя ждёт.',
    stock: 'bg-stock-oxblood',
    fabric: 'fabric-celestial',
    free: false,
    cardCount: 3,
    readingRoute: '/reading/destiny',
    content: {
      intro:
        'Расклад судьбы таро — для тех моментов, когда вопрос не в деталях, а в самом большом: «Зачем я здесь и куда иду?» Три карты показывают твой жизненный путь — не как предначертанную дорогу, а как карту возможностей. Первая карта — корни, то, что сформировало тебя. Вторая — текущий этап, урок, который ты проходишь прямо сейчас. Третья — направление, куда тебя зовёт внутренний компас. Этот расклад не про конкретное решение, а про масштаб. Он помогает подняться над рутиной и увидеть свою жизнь с высоты птичьего полёта. Когда кажется, что ты потеряла направление — три карты могут вернуть ощущение пути.',
      howItWorks: [
        'Отпусти конкретные вопросы — просто спроси себя: «Что мне важно понять о своём пути?»',
        'Вытяни 3 карты',
        'Кот раскроет историю твоего жизненного пути через символику карт',
        'Почувствуй, что откликается — интуиция здесь важнее логики',
      ],
      whatYouLearn: [
        'Основа: что сформировало твой характер и путь',
        'Текущий жизненный урок, который ты проходишь',
        'Направление развития и скрытый потенциал',
      ],
      whoIsItFor:
        'Для тебя, если чувствуешь, что живёшь не свою жизнь, или просто хочешь остановиться и осмыслить, к чему всё идёт. Если устала от мелких решений и хочется увидеть большую картину. Этот расклад для глубокой саморефлексии.',
      faq: [
        {
          question: 'Расклад судьбы покажет моё будущее?',
          answer:
            'Он покажет направление, но не конкретные события. Думай об этом как о компасе — он указывает на север, но путь выбираешь ты. Будущее создаёшь ты сама, карты лишь подсвечивают тенденции.',
        },
        {
          question: 'Мне страшно узнать свою судьбу. Это нормально?',
          answer:
            'Абсолютно нормально. Этот страх часто говорит о том, что ты на пороге важных перемен. Расклад судьбы мягкий — он не пугает, а помогает увидеть возможности. И помни: решение всегда остаётся за тобой.',
        },
      ],
    },
  },

  'rasklad-na-uchebu': {
    slug: 'rasklad-na-uchebu',
    id: 'education',
    title: 'Расклад на учебу',
    metaTitle: 'Расклад Таро на Учёбу Онлайн | Таро Мяу',
    metaDescription: 'Расклад таро на учебу — 5 карт покажут твой потенциал, препятствия и советы для обучения. Персональная интерпретация для студентов и не только.',
    stock: 'bg-stock-cobalt',
    fabric: 'fabric-tartan',
    free: false,
    cardCount: 5,
    readingRoute: '/reading/education',
    content: {
      intro:
        'Расклад таро на учебу — пять карт для тех, кто учится, переучивается или думает, стоит ли вообще начинать. Школа, университет, курсы, самообразование — форма не важна, важно то, что учёба занимает твои мысли. Может, сессия на носу и мотивация на нуле. Может, выбираешь между двумя специальностями. Или задумалась о том, чтобы всё бросить и начать учиться заново. Пять карт раскроют твой учебный потенциал, покажут, что тебе мешает, и подскажут, как извлечь максимум из процесса обучения. Это не про оценки и дипломы — это про то, как учёба вписывается в твою жизнь.',
      howItWorks: [
        'Подумай о своём учебном вопросе — конкретный предмет, выбор направления или мотивация',
        'Вытяни 5 карт',
        'Каждая карта раскроет свой аспект: потенциал, препятствия, ресурсы, метод, результат',
        'Кот даст персональные рекомендации по твоей ситуации',
      ],
      whatYouLearn: [
        'Твой реальный потенциал в выбранном направлении',
        'Что мешает учиться эффективно',
        'Какие ресурсы и подходы тебе подойдут лучше всего',
        'Чего ожидать от обучения в ближайшей перспективе',
      ],
      whoIsItFor:
        'Для тебя, если учёба — это не просто задания и экзамены, а большой вопрос о будущем. Если сомневаешься в выбранном пути, потеряла мотивацию или стоишь перед выбором нового направления. Расклад поможет взглянуть на обучение как на часть твоего пути.',
      faq: [
        {
          question: 'Можно ли по раскладу понять, какую профессию выбрать?',
          answer:
            'Расклад не назовёт конкретную профессию, но покажет, какие качества в тебе сейчас раскрываются и где твоя энергия. Это отличная отправная точка для размышлений, а не готовый ответ.',
        },
        {
          question: 'Расклад поможет перед экзаменом?',
          answer:
            'Он не заменит подготовку, но может снять тревожность и показать, на чём сфокусироваться. Многие отмечают, что после расклада становится спокойнее — а спокойствие на экзамене дорогого стоит.',
        },
        {
          question: 'Я давно не учусь, мне подойдёт этот расклад?',
          answer:
            'Учёба — это не только вуз. Онлайн-курсы, книги, новые навыки, смена профессии — всё это обучение. Расклад работает с любой формой роста и развития.',
        },
      ],
    },
  },

  'rasklad-na-byvshego': {
    slug: 'rasklad-na-byvshego',
    id: 'ex-partner',
    title: 'Расклад на бывшего',
    metaTitle: 'Расклад Таро на Бывшего Онлайн | Таро Мяу',
    metaDescription: 'Расклад таро на бывшего — 4 карты покажут его чувства, причины разрыва и шансы на воссоединение. Честный разбор без иллюзий.',
    stock: 'bg-stock-rose',
    fabric: 'fabric-dots',
    free: false,
    cardCount: 4,
    readingRoute: '/reading/ex-partner',
    quickDraw: false,
    content: {
      intro:
        'Расклад таро на бывшего — четыре карты для той ситуации, когда прошлое не отпускает. Расстались, но мысли всё ещё возвращаются к нему. Что он чувствует сейчас? Думает ли обо мне? Есть ли шанс? Или пора наконец отпустить? Эти вопросы могут крутиться в голове неделями, и никакие советы подруг не помогают. Четыре карты дадут честную картину: что осталось между вами, что чувствует он и какой путь сейчас будет лучшим для тебя. Этот расклад не обещает воссоединения и не рвёт по живому — он даёт ту ясность, которая нужна, чтобы двигаться дальше, в каком бы направлении ты ни решила идти.',
      howItWorks: [
        'Подумай о бывшем партнёре — вспомни его, но без боли, с лёгким любопытством',
        'Вытяни 4 карты',
        'Кот раскроет: его текущие чувства, что между вами осталось, препятствия, перспективы',
        'Получи совет, основанный на реальной энергии ситуации, а не на иллюзиях',
      ],
      whatYouLearn: [
        'Что он сейчас чувствует по отношению к тебе',
        'Реальные причины разрыва, которые вы могли не обсудить',
        'Есть ли потенциал для нового начала',
        'Какой путь принесёт тебе больше счастья',
      ],
      whoIsItFor:
        'Для тебя, если расставание ещё болит и ты не можешь перестать думать о нём. Если мечешься между «написать» и «забыть», и ни один вариант не приносит покоя. Этот расклад для тех, кому нужна честность, а не утешительная ложь.',
      faq: [
        {
          question: 'Расклад покажет, вернётся ли он?',
          answer:
            'Расклад покажет текущую энергию между вами и направление, в котором всё движется. Но «вернётся или нет» зависит от решений вас обоих. Карты дают перспективу, а не гарантии.',
        },
        {
          question: 'Я расстаюсь болезненно. Расклад не сделает хуже?',
          answer:
            'Мы стараемся дать ясность мягко, с заботой. Цель расклада — помочь тебе разобраться, а не бередить раны. Если чувствуешь, что сейчас не готова — подожди. Расклад никуда не денется.',
        },
      ],
    },
    // SEO pilot 2026-10 (docs/seo/roadmap-2026-10.md): reviewed copy — replaces the page's
    // meta, H1, intro, body sections and FAQ, and adds a worked example. Control pages have no `pilot`.
    pilot: {
      metaTitle: 'Расклад таро на бывшего: 4 карты о его чувствах | Таро Мяу',
      metaDescription: 'Его чувства, причина разрыва, где он сейчас и что дальше — четыре карты таро о бывшем. Честный разбор без ложных надежд и без приговоров.',
      h1: 'Расклад таро на бывшего: что осталось между вами',
      intro: 'Расклад таро на бывшего — четыре карты о том, что осталось после расставания: что он чувствует, почему всё закончилось, где он сейчас и куда движется ваша история. Расклад не обещает, что бывший вернётся, и не выносит приговор. Он помогает увидеть ситуацию целиком, когда мысли неделями ходят по кругу.\n\nВопрос формулировать не нужно: просто подумай о нём и вытяни карты. Таро Кот прочитает все четыре вместе и скажет, что из этого важно для тебя, а не для него.',
      sections: [
        {
          h2: 'Схема расклада: четыре позиции',
          paragraphs: [
            'Одна и та же карта звучит по-разному в зависимости от позиции. В конце кот собирает карты в один ответ: что это значит для тебя и какой шаг сейчас бережнее к себе — написать, подождать или отпустить.',
          ],
          bullets: [
            '«Его чувства» — что, по картам, он испытывает к тебе и к вашей истории сейчас.',
            '«Почему расстались» — что могло стоять за разрывом: возможно, то, что вы так и не проговорили.',
            '«Он сейчас» — в каком он состоянии: держится за старое, ищет новое или просто живёт дальше.',
            '«Что дальше» — куда движется ваша история, если всё останется как есть.',
          ],
        },
        {
          h2: 'Вопросы на бывшего: о чём думать, пока тянешь карты',
          paragraphs: [
            'Задавать вопрос здесь не нужно — позиции закрывают главное. Но если держать в голове то, что волнует именно тебя, проще заметить, что в ответе отзывается.',
            'Хочешь спросить своими словами — для этого есть «Задай вопрос». Короткий ответ на конкретный вопрос даст «Да или нет», только не спрашивай про сроки: карты говорят о направлении, а не о датах. А выбрать между «ждать» и «отпустить» поможет отдельный расклад.',
          ],
          bullets: [
            'Что между нами осталось и стоит ли за это держаться.',
            'Какая в нашем расставании моя часть.',
            'Как перестать ждать его сообщения.',
            'Есть ли у нас шанс, если мы оба что-то поменяем.',
          ],
          links: [
            { to: '/kak-zadavat-voprosy-taro', label: 'Как задавать вопросы таро' },
            { to: '/rasklady/zadaj-vopros', label: 'Расклад «Задай вопрос»' },
            { to: '/rasklady/da-ili-net', label: 'Расклад «Да или нет»' },
            { to: '/rasklady/zhdat-ili-otpustit', label: 'Расклад «Ждать или отпустить»' },
          ],
        },
        {
          h2: 'Вернётся ли бывший: что карты покажут, а что нет',
          paragraphs: [
            'Честный ответ на главный вопрос: карты не решают за двоих. Они показывают, что происходит между вами сейчас и куда это тянется. Вернуться или нет — выбор двух взрослых людей, и он может измениться.',
            'Если в позиции «Что дальше» выпала карта ухода или окончания, это не приговор, а повод перестать держать дверь открытой ценой своих сил. Тёплая карта — не гарантия, а знак, что разговор возможен. Решение в любом случае за тобой.',
          ],
        },
        {
          h2: 'Как читать карты таро о бывшем',
          paragraphs: [
            'Сначала посмотри на масти: они подсказывают, в какой плоскости лежит ответ.',
            'Старшие арканы — знак, что за историей стоит важный поворот для вас обоих, а не одна ссора. И читай карты не по одной, а вместе. Например, Шестерка Кубков рядом с Восьмеркой Кубков — это ностальгия, которая не держит, а подталкивает идти дальше.',
          ],
          bullets: [
            'Кубки — чувства, привязанность, нежность и тоска.',
            'Мечи — слова, ссоры и обиды.',
            'Пентакли — быт, деньги и договорённости.',
            'Жезлы — страсть, амбиции, желание действовать или сбежать.',
          ],
          links: [
            { to: '/karty/masti/kubki', label: 'Масть Кубков' },
            { to: '/karty/masti/mechi', label: 'Масть Мечей' },
            { to: '/karty/masti/pentakli', label: 'Масть Пентаклей' },
            { to: '/karty/masti/zhezly', label: 'Масть Жезлов' },
            { to: '/karty/sochetaniya', label: 'Сочетания карт таро' },
            { to: '/karty', label: 'Значения всех 78 карт' },
          ],
        },
        {
          h2: 'Карты, которые меняют смысл расклада',
          paragraphs: [
            'Частая ошибка — видеть в тяжёлой карте приговор, а в тёплой гарантию. Смотри на позицию: Башня в «Почему расстались» объясняет прошлое, а не предсказывает будущее.',
          ],
          bullets: [
            'Тройка Мечей — сердечная боль, которую нужно прожить, а не глушить.',
            'Двойка Кубков — взаимное чувство на равных; перевёрнутая — дисбаланс, когда один вкладывается больше.',
            'Башня — резкий слом того, что держалось на самообмане. Больно, но честно.',
            'Смерть — конец прежней формы отношений, а не обязательно чувств: старая динамика уходит, освобождая место новой.',
          ],
          links: [
            { to: '/karty/troyka-mechej', label: 'Тройка Мечей' },
            { to: '/karty/dvoyka-kubkov', label: 'Двойка Кубков' },
            { to: '/karty/bashnya', label: 'Башня' },
            { to: '/karty/smert', label: 'Смерть' },
          ],
        },
        {
          h2: 'Когда этот расклад подойдёт, а когда нужен другой',
          paragraphs: [
            'Расклад для тебя, если расставание ещё болит и ты мечешься между «написать» и «забыть», — или если спустя годы хочется понять, чему научила та история.',
            'Если вы не расставались, а он просто исчез без объяснений, точнее будет расклад «Почему он пропал». А чтобы разобраться в нынешних отношениях — любовный расклад.',
          ],
          links: [
            { to: '/rasklady/pochemu-on-propal', label: 'Почему он пропал' },
            { to: '/rasklady/lyubovnyj-rasklad', label: 'Любовный расклад' },
          ],
        },
      ],
      example: {
        question: 'Мы расстались в июле, а он до сих пор смотрит мои сторис. Вернётся ли он?',
        cards: [
          {
            cardSlug: 'shesterka-kubkov',
            position: 'Его чувства',
            text: 'Он вспоминает вас с теплом: Шестерка Кубков — карта ностальгии и нежности без расчёта. Сторис, скорее всего, отсюда, из памяти о хорошем. Но у карты есть тихая оговорка: тоска по тому, что было, — ещё не желание вернуть это сегодня.',
          },
          {
            cardSlug: 'pyaterka-mechej',
            position: 'Почему расстались',
            text: 'Пятерка Мечей говорит о ссорах, где важнее было оказаться правым, чем остаться рядом. Гордость и колкости понемногу съели близость. Здесь нет одного виноватого — есть цена, которую заплатили оба.',
          },
          {
            cardSlug: 'vosmerka-kubkov',
            position: 'Он сейчас',
            text: 'Восьмерка Кубков — тихий уход без драмы. Он не мстит и не играет, а идёт искать то, чего ему не хватало. Скучать и при этом уходить — вполне возможно.',
          },
          {
            cardSlug: 'zvezda',
            position: 'Что дальше',
            text: 'Звезда — карта надежды и исцеления после трудного периода. Для вашей истории это светлый знак: дверь не закрыта, и примирение не исключено. Но не сразу и не из сторис — сначала силы должны вернуться к тебе.',
          },
        ],
        summary: 'Он помнит хорошее, но сейчас идёт своим путём, а разрыв вырос из ссор, где каждый защищал свою правоту. Звезда оставляет надежду, но ждать сложа руки, пока просмотр сторис превратится в сообщение, карты не советуют. Сейчас важнее вернуть силы себе, а если разговор однажды случится — начинать его не с того, кто был прав.',
      },
      faq: [
        {
          question: 'Покажет ли расклад, вернётся ли бывший?',
          answer: 'Расклад покажет, что происходит между вами сейчас и куда это движется. Но вернуться или нет — решение двух людей, и карты его не принимают. Воспринимай ответ как направление, а не гарантию.',
        },
        {
          question: 'Нужны ли его фото, имя или дата рождения?',
          answer: 'Нет. Достаточно подумать о нём перед тем, как вытянуть карты.',
        },
        {
          question: 'Можно ли гадать на бывшего, если у него уже другая?',
          answer: 'Можно: карты подскажут, что с его чувствами и где он сейчас, а кот — что из этого важно для тебя. Только будь к себе бережной: если ответ может сильно ранить, сначала дай себе немного времени.',
        },
        {
          question: 'Как часто можно делать расклад на бывшего?',
          answer: 'Жёстких правил нет, но каждый день повторять его не стоит: ситуация не меняется так быстро, и карты начинают отражать тревогу, а не происходящее. Вернись к раскладу, когда что-то произойдёт — разговор, встреча, новое решение.',
        },
        {
          question: 'Что делать, если карты выпали тяжёлые?',
          answer: 'Тяжёлая карта — не приговор, а честный взгляд на ситуацию. Кот объяснит её бережно и подскажет, что сделать для себя. А если на душе совсем плохо, поговори с подругой или психологом — карты их не заменят.',
        },
      ],
    },
  },

  'pochemu-on-propal': {
    slug: 'pochemu-on-propal',
    id: 'disappearance',
    title: 'Почему он пропал?',
    metaTitle: 'Почему Он Пропал — Таро Расклад | Таро Мяу',
    metaDescription: 'Почему он пропал таро — 3 карты раскроют причины молчания и его настоящие чувства. Узнай правду, которую он не говорит.',
    stock: 'bg-stock-lilac',
    fabric: 'fabric-linen',
    free: false,
    cardCount: 3,
    readingRoute: '/reading/disappearance',
    content: {
      intro:
        'Почему он пропал — таро расклад для ситуации, знакомой до боли: всё шло хорошо, и вдруг — тишина. Не пишет, не звонит, исчез без объяснений. Ты перечитываешь последние сообщения, ищешь, что сделала не так, и не находишь ответа. Три карты помогут увидеть то, что скрыто за молчанием. Причины могут быть неожиданными: его страхи, сомнения, внешние обстоятельства, которые ты не могла знать. Этот расклад не оправдывает и не обвиняет — он показывает реальную картину. И часто самое ценное, что ты из него узнаёшь, — это не про него, а про себя: что тебе по-настоящему нужно и чего ты заслуживаешь.',
      howItWorks: [
        'Вспомни этого человека — без злости, с желанием понять',
        'Вытяни 3 карты',
        'Кот раскроет: реальную причину, его внутреннее состояние и что ждёт впереди',
        'Обрати внимание на совет — он часто говорит больше, чем сами причины',
      ],
      whatYouLearn: [
        'Настоящая причина его молчания',
        'Что он чувствует, но не может или не хочет сказать',
        'Чего ожидать: вернётся он или нет',
      ],
      whoIsItFor:
        'Для тебя, если его молчание сводит с ума и ты устала от неизвестности. Если хочешь перестать гадать и наконец получить хотя бы какую-то ясность. Этот расклад для тех, кому нужна правда — даже если она непростая.',
      faq: [
        {
          question: 'Он пропал неделю назад. Не рано ли делать расклад?',
          answer:
            'Нет жёстких правил. Если его молчание тебя мучает — расклад поможет прямо сейчас. Иногда неделя тишины — это вечность, и ты заслуживаешь ясности.',
        },
        {
          question: 'Что делать, если расклад говорит, что он не вернётся?',
          answer:
            'Помни, что карты показывают направление, а не приговор. Но если расклад подтверждает то, что ты уже чувствовала, — это повод отпустить и направить энергию на себя. Ты заслуживаешь того, кто не пропадает.',
        },
      ],
    },
  },

  'zhdat-ili-otpustit': {
    slug: 'zhdat-ili-otpustit',
    id: 'wait-or-release',
    title: 'Ждать или отпустить',
    metaTitle: 'Ждать или Отпустить — Таро Расклад | Таро Мяу',
    metaDescription: 'Ждать или отпустить таро — 5 карт помогут решить, стоит ли ждать или пора двигаться дальше. Глубокий разбор твоей ситуации.',
    stock: 'bg-stock-mauve',
    fabric: 'fabric-seersucker',
    free: false,
    cardCount: 5,
    readingRoute: '/reading/wait-or-release',
    content: {
      intro:
        'Ждать или отпустить — таро расклад на пять карт для одной из самых мучительных дилемм. Ждать, что он напишет, что ситуация изменится, что всё наладится само? Или хватит — пора отпустить и идти дальше? Этот вопрос может касаться отношений, работы, мечты, которая никак не сбывается, или любого другого подвешенного состояния. Пять карт помогут увидеть, что стоит за твоим ожиданием — надежда или страх? Что ты теряешь, пока ждёшь? И что тебя ждёт, если решишься отпустить. Это расклад для тех, кто устал от подвешенного состояния и хочет наконец сдвинуться с мёртвой точки.',
      howItWorks: [
        'Подумай о ситуации, в которой ты ждёшь и сомневаешься',
        'Вытяни 5 карт',
        'Кот проанализирует: суть ожидания, что ты теряешь, что ждёт при каждом выборе, совет',
        'Сравни два пути — ожидание и отпускание — и прислушайся к себе',
      ],
      whatYouLearn: [
        'Истинная причина, почему ты ждёшь — надежда или привычка',
        'Что ты теряешь, пока находишься в подвешенном состоянии',
        'Что произойдёт, если продолжишь ждать',
        'Что откроется, если решишь отпустить',
        'Конкретный совет для твоей ситуации',
      ],
      whoIsItFor:
        'Для тебя, если чувствуешь себя в ловушке ожидания — не можешь ни ждать спокойно, ни уйти. Если эта подвешенность высасывает силы и ты понимаешь, что так больше нельзя. Расклад не примет решение за тебя, но покажет то, что ты, возможно, боишься увидеть.',
      faq: [
        {
          question: 'Расклад скажет мне, что делать?',
          answer:
            'Не скажет — и это намеренно. Он покажет последствия обоих вариантов, но решение всегда остаётся за тобой. Задача таро — дать ясность, а не инструкции.',
        },
        {
          question: 'Можно использовать этот расклад не для отношений?',
          answer:
            'Конечно! «Ждать или отпустить» работает для любой ситуации: ожидание повышения, ответа от университета, исполнения мечты. Если ты в подвешенном состоянии — этот расклад для тебя.',
        },
        {
          question: 'Что если расклад показал «ждать», а я хочу отпустить?',
          answer:
            'Твоё внутреннее ощущение — тоже ответ. Если расклад говорит «подожди», а ты чувствуешь, что хватит, — возможно, тебе нужен был именно этот толчок. Карты дают перспективу, а ты выбираешь.',
        },
      ],
    },
  },

  'gadanie-na-situaciyu': {
    slug: 'gadanie-na-situaciyu',
    id: 'situation',
    title: 'Гадание на ситуацию',
    metaTitle: 'Гадание на Ситуацию Таро Онлайн | Таро Мяу',
    metaDescription: 'Гадание на ситуацию таро — 4 карты дадут полный анализ текущих обстоятельств, скрытых влияний и возможных исходов. Персональная интерпретация.',
    stock: 'bg-stock-moss',
    fabric: 'fabric-waffle',
    free: false,
    cardCount: 4,
    readingRoute: '/reading/situation',
    content: {
      intro:
        'Гадание на ситуацию таро — универсальный расклад на четыре карты для тех случаев, когда конкретный вопрос не формулируется, а ситуация просто «давит». Конфликт на работе, запутанная история с друзьями, семейная драма, неопределённость, которая никак не разрешается. Четыре карты дадут объёмный анализ: что на самом деле происходит, что ты не видишь, какие силы работают за кулисами и к чему всё движется. Иногда достаточно просто увидеть ситуацию со стороны — как если бы кто-то нарисовал карту твоей жизни и показал: вот ты здесь, а вот куда ведут тропинки.',
      howItWorks: [
        'Опиши ситуацию — своими словами, как чувствуешь',
        'Вытяни 4 карты',
        'Кот проанализирует: суть ситуации, скрытые факторы, совет и вероятный исход',
        'Обрати внимание на скрытые факторы — они часто оказываются ключом ко всему',
      ],
      whatYouLearn: [
        'Реальная суть ситуации, очищенная от эмоций',
        'Скрытые влияния и факторы, которые ты не учитывала',
        'Практический совет: что делать прямо сейчас',
        'Наиболее вероятный исход при текущем развитии событий',
      ],
      whoIsItFor:
        'Для тебя, если ситуация запутанная и ты не знаешь, с какого конца за неё браться. Если кажется, что-то важное ускользает от понимания. Этот расклад — как разговор с мудрым другом, который видит то, что ты не замечаешь.',
      faq: [
        {
          question: 'Чем этот расклад отличается от «Задай вопрос»?',
          answer:
            '«Задай вопрос» — это точечный ответ на конкретный вопрос. «Гадание на ситуацию» — это объёмный анализ целой ситуации: причины, скрытые факторы, совет и исход. Используй его, когда вопрос шире одного предложения.',
        },
        {
          question: 'Можно ли делать расклад на рабочую ситуацию?',
          answer:
            'Конечно! Этот расклад универсален — он работает с любой жизненной ситуацией: личной, рабочей, семейной. Главное — честно описать, что тебя волнует.',
        },
      ],
    },
  },

  'uvolnyatsya-nelzya-ostatsya': {
    slug: 'uvolnyatsya-nelzya-ostatsya',
    id: 'career-crossroads',
    title: 'Увольняться нельзя остаться',
    metaTitle: 'Увольняться или Остаться — Таро | Таро Мяу',
    metaDescription: 'Увольняться таро расклад — 6 карт помогут разобраться в карьерном выборе. Подробный разбор обоих путей: остаться на работе или уйти.',
    stock: 'bg-stock-fern',
    fabric: 'fabric-basket',
    free: false,
    cardCount: 6,
    readingRoute: '/reading/career-crossroads',
    content: {
      intro:
        'Увольняться нельзя остаться — расклад на шесть карт для тех, кто мучается карьерным выбором. Знакомое чувство: утром не хочется вставать на работу, но страшно уходить в неизвестность. Или наоборот — горишь новой идеей, но разумная часть шепчет «а вдруг не получится?». Шесть карт покажут обе стороны: что тебя держит на текущем месте, что тянет прочь, какие возможности ты не видишь и чего стоит бояться на самом деле. Это расклад для взрослого, осмысленного решения — не импульсивного «всё бросить» и не пассивного «потерплю ещё годик». Где поставить запятую в этом предложении — решишь ты.',
      howItWorks: [
        'Подумай о своей рабочей ситуации — что держит и что не устраивает',
        'Вытяни 6 карт',
        'Кот проанализирует три грани каждого пути: «остаться» и «уйти»',
        'Сравни оба варианта и почувствуй, какой откликается сильнее',
        'Помни: расклад даёт перспективу, а решение — твоё',
      ],
      whatYouLearn: [
        'Что на самом деле держит тебя на текущем месте',
        'Реальные перспективы при уходе',
        'Скрытые возможности, которые ты не рассматривала',
        'Чего стоит бояться, а что — пустые страхи',
        'Какой путь больше соответствует твоим ценностям',
      ],
      whoIsItFor:
        'Для тебя, если мысль об увольнении не отпускает, но решиться не получается. Если устала от работы, но страх неизвестности парализует. Или если получила предложение, но не знаешь, стоит ли рисковать. Этот расклад — для тех, кто стоит на карьерном перекрёстке.',
      faq: [
        {
          question: 'А если у меня нет запасного варианта?',
          answer:
            'Расклад поможет и в этом случае — он покажет не только «уйти или остаться», но и скрытые возможности, которые ты могла не замечать. Иногда карты подсвечивают третий путь, о котором ты не думала.',
        },
        {
          question: 'Стоит ли увольняться, если расклад показал сложности?',
          answer:
            'Сложности будут на любом пути — это нормально. Вопрос в том, какие сложности ты готова преодолевать. Расклад помогает увидеть разницу между продуктивным вызовом и тупиком.',
        },
        {
          question: 'Подходит ли этот расклад для фриланса или своего дела?',
          answer:
            'Абсолютно. «Увольняться нельзя остаться» — это любой карьерный перекрёсток: уйти в своё дело, сменить профессию, перейти на фриланс, вернуться в найм. Расклад адаптируется под твою ситуацию.',
        },
      ],
    },
  },

  'rasklad-na-talant': {
    slug: 'rasklad-na-talant',
    id: 'talent',
    title: 'Расклад на талант',
    metaTitle: 'Расклад Таро на Таланты Онлайн | Таро Мяу',
    metaDescription: 'Расклад таро на таланты — раскрой скрытые способности с помощью 5 карт. Кот покажет твои сильные стороны и как их развить.',
    stock: 'bg-stock-ochre',
    fabric: 'fabric-fairisle',
    free: false,
    cardCount: 5,
    readingRoute: '/reading/talent',
    content: {
      intro:
        'Расклад таро на таланты — пять карт, которые помогут увидеть то, что ты сама в себе недооцениваешь. У каждого человека есть способности, которые он не замечает, — они кажутся такими естественными, что ты думаешь: «Так все могут». Нет, не все. Этот расклад подсвечивает твои сильные стороны, скрытые таланты и то, что мешает тебе их раскрыть. Может, ты отлично чувствуешь людей, но не считаешь это навыком. Или у тебя есть творческая жилка, которую ты задвинула ради «нормальной работы». Пять карт покажут, где твоя уникальность и как превратить её в нечто осязаемое.',
      howItWorks: [
        'Спроси себя: «Что я делаю лучше других, даже не замечая этого?»',
        'Вытяни 5 карт',
        'Кот раскроет: главный талант, скрытую способность, блоки, путь развития и потенциал',
        'Подумай, как применить увиденное в реальной жизни',
      ],
      whatYouLearn: [
        'Твой главный талант — то, что даётся тебе легче, чем другим',
        'Скрытая способность, которую ты пока не раскрыла',
        'Что блокирует проявление твоих талантов',
        'Конкретные шаги для развития',
        'Каким может быть результат, если ты пойдёшь этим путём',
      ],
      whoIsItFor:
        'Для тебя, если ты чувствуешь, что живёшь не в полную силу, но не понимаешь, чего не хватает. Если ищешь своё призвание или хочешь лучше понять свои сильные стороны. Этот расклад для тех, кто готов удивиться себе.',
      faq: [
        {
          question: 'Что если расклад покажет талант, о котором я не подозревала?',
          answer:
            'Это и есть лучший результат! Часто мы не видим свои сильные стороны именно потому, что они для нас «обычные». Отнесись к неожиданному ответу с любопытством — попробуй примерить его на себя.',
        },
        {
          question: 'Я уже знаю свои таланты. Зачем мне этот расклад?',
          answer:
            'Расклад может показать скрытые грани того, что ты уже знаешь, или подсветить способности, которые ты обесценивала. Плюс, он покажет, что мешает тебе реализовать потенциал на полную — а это часто самое ценное.',
        },
      ],
    },
  },

  'rasklad-na-samopoznanie': {
    slug: 'rasklad-na-samopoznanie',
    id: 'self-discovery',
    title: 'Расклад на самопознание',
    metaTitle: 'Расклад Самопознание Таро Онлайн | Таро Мяу',
    metaDescription: 'Расклад самопознание таро — 4 карты для глубокого анализа себя. Узнай свои истинные желания, страхи и путь к внутренней гармонии.',
    stock: 'bg-stock-slate',
    fabric: 'fabric-pinstripe',
    free: false,
    cardCount: 4,
    readingRoute: '/reading/self-discovery',
    content: {
      intro:
        'Расклад самопознание таро — четыре карты для тех, кто хочет заглянуть глубже привычных масок и ролей. Кто ты, когда никто не смотрит? Чего ты хочешь на самом деле, а не потому что «так принято»? Какие страхи управляют твоими решениями? Этот расклад — как честный разговор с собой, только с подсказками. Четыре карты раскроют твоё истинное «я», осветят тени — те части, которые ты прячешь или не принимаешь, и покажут путь к внутренней гармонии. Это не быстрый ответ на конкретный вопрос — это приглашение к глубокой рефлексии. Если чувствуешь, что потеряла связь с собой — этот расклад поможет её восстановить.',
      howItWorks: [
        'Отпусти все конкретные вопросы и просто побудь с собой',
        'Вытяни 4 карты',
        'Кот раскроет: твоё истинное «я», скрытые страхи, силу, которую ты не используешь, и путь вперёд',
        'Не спеши с выводами — дай себе время переварить увиденное',
      ],
      whatYouLearn: [
        'Кто ты под слоями социальных ролей и ожиданий',
        'Скрытые страхи и убеждения, которые ограничивают тебя',
        'Твоя настоящая сила — то, на что можно опереться',
        'Следующий шаг на пути к себе',
      ],
      whoIsItFor:
        'Для тебя, если чувствуешь, что потеряла себя в чужих ожиданиях. Если интуиция подсказывает, что ты живёшь не совсем свою жизнь. Если готова к честности — не всегда комфортной, но освобождающей. Этот расклад для тех, кто ищет не ответы на внешние вопросы, а связь с собой.',
      faq: [
        {
          question: 'Чем расклад на самопознание отличается от расклада судьбы?',
          answer:
            'Расклад судьбы смотрит на жизненный путь — откуда ты пришла и куда идёшь. Расклад на самопознание фокусируется на внутреннем мире: кто ты, что чувствуешь, что прячешь. Первый — про путь, второй — про сущность.',
        },
        {
          question: 'Мне страшно узнать про себя что-то неприятное.',
          answer:
            'Это нормальный страх, и он говорит о том, что ты готова к росту. Расклад показывает не «плохое» и «хорошее», а полную картину. Тени — это не недостатки, а непринятые части тебя, которые ждут интеграции.',
        },
        {
          question: 'Как часто стоит делать расклад на самопознание?',
          answer:
            'Раз в месяц-два — оптимально. Это глубокий расклад, и ему нужно время, чтобы «улечься». Используй промежутки для наблюдения: как откликается то, что показали карты, в реальной жизни.',
        },
      ],
    },
  },
};

export const SPREAD_SLUGS = Object.keys(SPREAD_SEO_DATA);
